import {
  partialDateFromBackend,
  formatPartialDate,
  PRECISION
} from '../../../../util/partialDate';
import { calculateAgePartial, formatAgeDisplay } from '../../../../util/util';
import {
  resolveCarrierState,
  carrierDisplay,
  CARRIER_STATE
} from './carrierState';

const isBlank = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim() === '') ||
  (Array.isArray(v) && v.length === 0);

function formatScalar(v) {
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.filter((x) => !isBlank(x)).join(', ');
  return String(v);
}

/** Format a plain (non-partial) date value as DD/MM/YYYY, best-effort. */
function formatPlainDate(v) {
  if (isBlank(v)) return '';
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}

/**
 * @param {object} descriptor  MergeFieldDescriptor { token, path, kind, partial? }
 * @param {*} raw              raw value from the record (shape depends on kind)
 * @returns {object} TokenResolution
 */
export function resolveToken(descriptor, raw) {
  if (!descriptor) {
    // Fail-closed: a token not in the catalogue is an error, never a blank.
    return { state: 'error', reason: 'unknown-token' };
  }

  // A RETIRED field (backend/data/retired-merge-fields.json) is the one
  // deliberate blank: the field no longer exists, so a saved template that
  // still names it renders nothing there — whatever a legacy record still
  // holds — and reads as empty to an IF, rather than as an error.
  if (descriptor.retired) {
    return { state: 'empty', reason: 'retired' };
  }

  if (descriptor.kind === 'clinical_assertion') {
    const state = resolveCarrierState(raw);
    if (state === CARRIER_STATE.UNKNOWN) {
      // 'unknown' is a comparable value but NOT a resolved assertion. text is
      // exposed so an explicit {{#if ... == 'unknown'}} branch can match.
      return { state: 'unknown', text: CARRIER_STATE.UNKNOWN };
    }
    return { state: 'resolved', text: state, display: carrierDisplay(state) };
  }

  if (descriptor.kind === 'date') {
    // Age is derived from DOB (+ DOD) via PartialDate → "45"/"~45"/"45–46".
    if (descriptor.path === 'age') {
      if (!raw || isBlank(raw.iso)) return { state: 'empty' };
      const dob = partialDateFromBackend({
        iso: raw.iso,
        estimatedFlag: raw.estimated,
        precisionField: raw.precision
      });
      if (!dob) return { state: 'empty' };
      const dod =
        raw.isAlive === false || raw.isAlive === 'false'
          ? partialDateFromBackend({
              iso: raw.dod,
              estimatedFlag: raw.estimatedDod,
              precisionField: raw.dodPrecision
            })
          : null;
      const result = calculateAgePartial(dob, dod);
      const text = formatAgeDisplay(result);
      if (!text) return { state: 'empty' };
      return { state: 'resolved', text, approximate: !result.isExact };
    }

    if (descriptor.partial) {
      const iso = raw && typeof raw === 'object' ? raw.iso : raw;
      if (isBlank(iso)) return { state: 'empty' };
      const partial = partialDateFromBackend({
        iso,
        estimatedFlag: raw && raw.estimated,
        precisionField: raw && raw.precision
      });
      if (!partial) return { state: 'empty' };
      const text = formatPartialDate(partial);
      return {
        state: 'resolved',
        text,
        approximate: partial.precision !== PRECISION.FULL
      };
    }

    // plain full date
    const iso = raw && typeof raw === 'object' ? raw.iso : raw;
    if (isBlank(iso)) return { state: 'empty' };
    const text = formatPlainDate(iso);
    return text ? { state: 'resolved', text } : { state: 'empty' };
  }

  // scalar
  if (isBlank(raw)) return { state: 'empty' };
  return { state: 'resolved', text: formatScalar(raw) };
}

function normalize(v) {
  return String(v == null ? '' : v)
    .trim()
    .toLowerCase();
}

function toNumber(v) {
  if (v == null || (typeof v === 'string' && v.trim() === '')) return null;
  const n = Number(String(v).trim());
  return Number.isFinite(n) ? n : null;
}

/**
 * Evaluate a parsed condition against a resolved token (brief §8.2 semantics).
 *
 * The single most important rule: when a CLINICAL ASSERTION resolves to
 * 'unknown', NO equality/inequality branch is satisfied except an explicit
 * `== 'unknown'`. There is never an implicit fallthrough to a negative.
 *
 * @param {object} cond        { field, op, value? }
 * @param {object} descriptor  descriptor for the condition field (may be a collection)
 * @param {object} resolution  TokenResolution, OR { isCollection:true, length } for collections
 * @returns {boolean}
 */
export function evaluateCondition(cond, descriptor, resolution) {
  if (!cond) return false;

  // Collection emptiness/presence.
  if (resolution && resolution.isCollection) {
    const nonEmpty = resolution.length > 0;
    if (cond.op === 'notEmpty' || cond.op === 'truthy') return nonEmpty;
    if (cond.op === 'empty') return !nonEmpty;
    return false; // eq/ne make no sense on a collection
  }

  const isClinical = descriptor && descriptor.kind === 'clinical_assertion';
  const state = resolution ? resolution.state : 'error';
  const text = resolution ? resolution.text : undefined;

  // Safety guard: an unknown clinical assertion matches ONLY `== 'unknown'`.
  if (isClinical && state === 'unknown') {
    if (cond.op === 'eq') return String(cond.value) === 'unknown';
    // notEmpty / empty / truthy / ne all FALSE — unknown asserts nothing.
    return false;
  }

  switch (cond.op) {
    case 'truthy':
      return (
        state === 'resolved' &&
        text !== undefined &&
        text !== '' &&
        text !== 'false' &&
        text !== 'No' &&
        text !== '0'
      );
    case 'notEmpty':
      return state === 'resolved';
    case 'empty':
      return state === 'empty';
    case 'eq':
      return state === 'resolved' && normalize(text) === normalize(cond.value);
    case 'ne':
      // Only meaningful when a value is present; an empty/unknown field does
      // not "not-equal" anything (avoids asserting on absence).
      return state === 'resolved' && normalize(text) !== normalize(cond.value);
    case 'contains':
      return (
        state === 'resolved' && normalize(text).includes(normalize(cond.value))
      );
    case 'notContains':
      // Like `ne`: only a present value can be said to "not contain" something.
      return (
        state === 'resolved' && !normalize(text).includes(normalize(cond.value))
      );
    case 'startsWith':
      return (
        state === 'resolved' &&
        normalize(text).startsWith(normalize(cond.value))
      );
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      if (state !== 'resolved') return false;
      const a = toNumber(text);
      const b = toNumber(cond.value);
      // Fail-closed: a non-numeric operand (e.g. an approximate age like "~45")
      // never satisfies a numeric comparison rather than asserting wrongly.
      if (a === null || b === null) return false;
      if (cond.op === 'gt') return a > b;
      if (cond.op === 'gte') return a >= b;
      if (cond.op === 'lt') return a < b;
      return a <= b;
    }
    default:
      return false;
  }
}
